/**
 * AdminLoginPage — dedicated admin/superadmin entry.
 *
 * Lives at /housekeeping/login. Strictly separated from the normal /login
 * flow: no Turnstile widget, no free-user signup affordance. Only hits the
 * dedicated POST /api/admin/login endpoint, which enforces role server-side.
 *
 * 2FA is handled via the existing /api/admin/2fa/verify-login and
 * /api/admin/2fa/recovery endpoints — same wire protocol as the unified
 * login, just reached through an admin-only entry.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Loader2, AlertTriangle } from "lucide-react";

export default function AdminLoginPage() {
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/housekeeping";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Mount-time session validation gate. We do NOT trust local cookies / the
  // shared AuthContext on this admin entry — we re-check `/auth/me` server
  // side and inspect the role explicitly. Stale non-admin sessions are
  // cleared immediately so they cannot leak into /housekeeping flows.
  const [validating, setValidating] = useState(true);

  // 2FA state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        if (data && (data.role === "admin" || data.role === "superadmin")) {
          // Already a valid admin → skip the login form.
          const target = nextPath && nextPath.startsWith("/housekeeping") ? nextPath : "/housekeeping";
          navigate(target, { replace: true });
          return;
        }
        // Authenticated but NOT admin → wipe the session so the login form
        // starts from a clean state. Otherwise the user would silently keep
        // a non-admin cookie that the standard login flow could resurrect.
        if (data) {
          try { await logout(); } catch { /* ignore */ }
        }
      } catch {
        // Unauthenticated / invalid token → expected, just show the form.
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nextPath, navigate, logout]);

  const redirectToTarget = () => {
    const target = nextPath && nextPath.startsWith("/housekeeping") ? nextPath : "/housekeeping";
    navigate(target);
  };

  // Stale 2FA challenge cleanup — wipes the temp token + form state so the
  // user can restart the email/password step without a hard refresh.
  const resetLoginFlow = () => {
    setTwoFAStep(false);
    setTempToken("");
    setTotpCode("");
    setRecoveryCode("");
    setUseRecovery(false);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      if (data?.requires_2fa) {
        setTempToken(data.temp_token);
        setTwoFAStep(true);
      } else {
        await refreshUser();
        redirectToTarget();
      }
    } catch (err) {
      // Always show the generic server message (or a fallback) — no role leak.
      setError(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = useRecovery ? "/admin/2fa/recovery" : "/admin/2fa/verify-login";
      const normalizedRecovery = recoveryCode.replace(/[^0-9a-fA-F]/g, "").toLowerCase();
      const payload = useRecovery
        ? { temp_token: tempToken, recovery_code: normalizedRecovery }
        : { temp_token: tempToken, code: totpCode };
      await api.post(endpoint, payload);
      await refreshUser();
      redirectToTarget();
    } catch (err) {
      setError(err?.response?.data?.detail || (useRecovery ? "Invalid recovery code" : "Invalid TOTP code"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4">
      {validating ? (
        <div className="flex flex-col items-center gap-3" data-testid="admin-login-validating">
          <Loader2 size={20} className="animate-spin text-[#888888]" />
          <p className="text-xs text-[#888888]">Checking session…</p>
        </div>
      ) : (
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Shield size={18} className="text-[#555555]" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-[#555555]">Housekeeping</span>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
          <div>
            <h1 className="text-lg font-semibold text-[#111111] flex items-baseline gap-2.5" data-testid="admin-login-heading">
              Sign in
              <span className="text-xs font-normal text-[#888888]" data-testid="admin-login-heading-context">Housekeeping</span>
            </h1>
            <p className="text-xs text-[#888888] mt-1">
              Restricted to operators. Standard users: <Link to="/login" className="underline hover:text-[#111111]">normal login</Link>.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="admin-login-error">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!twoFAStep ? (
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="admin-login-form">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-[#555555]">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  data-testid="admin-login-email"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-[#555555]">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="admin-login-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="admin-login-submit"
              >
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Sign in
              </Button>
            </form>
          ) : (
            <form onSubmit={handle2FASubmit} className="space-y-3" data-testid="admin-login-2fa-form">
              <div className="text-xs text-[#555555]">Two-factor authentication required.</div>
              {!useRecovery ? (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-[#555555]">Authenticator code</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    required
                    autoFocus
                    placeholder="123456"
                    data-testid="admin-login-totp"
                  />
                </div>
              ) : (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-[#555555]">Recovery code</label>
                  <Input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    required
                    autoFocus
                    placeholder="e.g. a1b2c3d4"
                    autoComplete="one-time-code"
                    spellCheck={false}
                    autoCapitalize="off"
                    className="font-mono"
                    data-testid="admin-login-recovery"
                  />
                  <p className="text-[10px] text-[#888888] mt-1">8-character code from your saved list.</p>
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || (useRecovery ? !recoveryCode : totpCode.length !== 6)}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="admin-login-2fa-submit"
              >
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Verify
              </Button>
              <button
                type="button"
                className="text-[11px] text-[#888888] hover:text-[#555555] underline-offset-2 hover:underline"
                onClick={() => { setUseRecovery((v) => !v); setError(""); setTotpCode(""); setRecoveryCode(""); }}
                data-testid="admin-login-toggle-recovery"
              >
                {useRecovery ? "Use authenticator app instead" : "Use recovery code instead"}
              </button>
              <button
                type="button"
                className="block text-[11px] text-[#888888] hover:text-[#555555] underline-offset-2 hover:underline"
                onClick={resetLoginFlow}
                data-testid="admin-login-restart"
              >
                Restart login flow
              </button>
            </form>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
